import type { DatabaseDictionary } from "./db";
import type { IStreamOutputDB } from "../interfaces/Streaming";

const STREAM_USER_ID_KEY = "@Eriol/system/userId";
const LEGACY_USER_ID = "__legacy__";

function getCurrentUserId(): string | null {
  return localStorage.getItem(STREAM_USER_ID_KEY);
}

function isCurrentUserRecord(record: IStreamOutputDB, userId: string): boolean {
  return record.userId === userId;
}

function isLegacyRecord(record: IStreamOutputDB): boolean {
  return record.userId === LEGACY_USER_ID;
}

export async function getScopedStreamOutputs(db: DatabaseDictionary): Promise<IStreamOutputDB[]> {
  const currentUserId = getCurrentUserId();
  if (!currentUserId) {
    return [];
  }

  const scopedRecords = await db.streamOutput.where("userId").equals(currentUserId).toArray();
  if (scopedRecords.length > 0) {
    return scopedRecords;
  }

  // Migration fallback: move legacy records to the currently logged user.
  const legacyRecords = await db.streamOutput.where("userId").equals(LEGACY_USER_ID).toArray();
  if (legacyRecords.length === 0) {
    return [];
  }

  await db.transaction("rw", db.streamOutput, async () => {
    for (const record of legacyRecords) {
      if (record.id != null) {
        await db.streamOutput.update(record.id, { userId: currentUserId });
      }
    }
  });

  return legacyRecords.map((record) => ({ ...record, userId: currentUserId }));
}

export async function saveScopedStreamOutputs(
  db: DatabaseDictionary,
  entries: Array<Pick<IStreamOutputDB, "name" | "stream">>
): Promise<void> {
  const currentUserId = getCurrentUserId();
  if (!currentUserId) {
    throw new Error("No authenticated user for stream output settings");
  }

  await db.transaction("rw", db.streamOutput, async () => {
    const allRecords = await db.streamOutput.toArray();

    for (const record of allRecords) {
      if (record.id == null) continue;
      if (isCurrentUserRecord(record, currentUserId) || isLegacyRecord(record)) {
        await db.streamOutput.delete(record.id);
      }
    }

    for (const entry of entries) {
      await db.streamOutput.add({
        userId: currentUserId,
        name: entry.name,
        stream: entry.stream,
      });
    }
  });
}
